import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import $modal from '@core/services/modal';
import auth from '@core/services/auth';
import { cacheAdapter } from '@core/services/cacheAdapter';
import modalInfo from '@core/modals/modalInfo.vue';
import { notify } from '@kyvg/vue3-notification';
import settings from 'settings';
import util from '@core/services/util';

const UNAUTHORIZED = 401;
const cancelTokens: any = {};

const httpClient = axios.create({
	headers: {
		'Cache-Control': 'no-cache',
		'Content-Type': 'application/json',
	},
	adapter: cacheAdapter(axios.defaults.adapter, { enabledByDefault: false }),
});

httpClient.defaults.headers.post['Access-Control-Allow-Origin'] = '*';
httpClient.defaults.headers.get['Access-Control-Allow-Origin'] = '*';

function getVersionJsonFile() {
	return axios.get('/dist/version.json', {
		headers: { 'Cache-Control': 'no-cache' },
	});
}
async function checkIfVersionHasChanged() {
	const userCurrentVersion = localStorage.getItem('CurrentVersion');
	const response = await getVersionJsonFile();
	const systemCurrentVersion = response.data.version;

	if (userCurrentVersion != null && systemCurrentVersion != userCurrentVersion) {
		$modal.open(modalInfo, {
			name: 'modalInfo',
			passedData: {
				info: 'You need to refresh to get the latest version. Version: ' + systemCurrentVersion,
				acceptText: 'OK',
			},
			backdrop: false,
			postFunction: () => {
				auth.logout(false);
				location.reload();
			},
		});
		return true;
	}

	return false;
}

//ON REFRESH, WE SET THE CURRENT VERSION
localStorage.removeItem('CurrentVersion');
if (settings.authRequired) {
	getVersionJsonFile().then(response => {
		localStorage.setItem('CurrentVersion', response.data.version);
	});
}

// class CustomError extends Error {
//     constructor(message?: any) {
//         super(message);
//         this.name = "SaturnBrowserError"
//     }
// }

httpClient.interceptors.request.use(async function (config) {
	////If this is a SaturnBrowser request but SaturnBrowser isn't being used, throw Error
	//if (config.url.includes(settings.saturnBrowserApiUrl) && !navigator.webdriver) {
	//    const error = new CustomError("Saturn Browser is required.");
	//    error.config = config;
	//    return Promise.reject(error);
	//}

	//ADDS AUTH TOKEN TO EVERY REQUEST
	if (settings.authRequired) {
		//ON EVERY REQUEST, WE CHECK IF THE VERSION HAS CHANGED
		const didVersionChange = await checkIfVersionHasChanged();
		if (didVersionChange) {
			throw new axios.Cancel('Version of saturn app changed. Update required.');
		}

		if (auth.checkToken()) {
			config.headers['Authorization'] = 'Bearer ' + auth.getToken();
		} else {
			auth.logout(true);
		}
	}

	return config;
});

httpClient.interceptors.response.use(
	response => {
		if (response && response.status == 204) {
			//empty response should return null
			response.data = null;
		}

		return response;
	},
	function (error) {
		console.error('Error Details', error);
		const status = error.response?.status;

		notify({
			group: 'saturn',
			title: 'Error Occurred',
			type: 'error',
			text: util.formatToastrText(error),
			closeOnClick: true,
		});

		if (status === UNAUTHORIZED && settings.authRequired) {
			auth.logout(true);
		}

		return Promise.reject(error.response);
	},
);

//THIS WILL DELETE FRONTEND ONLY PROPERTIES FROM THE REQUEST BODY, THIS IS DONE TO AVOID SENDING POINTLESS DATA ACROSS
function sanitizeData(data?: any) {
	//FIXME: Stop calling things data!

	if (data == null || Array.isArray(data)) return data;

	if (data.getSanitizedData) data = data.getSanitizedData();
	if (data.deal?.getSanitizedData) data.deal = data.deal.getSanitizedData();
	if (data.fimenu?.getSanitizedData) data.fimenu = data.fimenu.getSanitizedData();
	if (data.customer?.getSanitizedData) data.customer = data.customer.getSanitizedData();
	if (data.coCustomer?.getSanitizedData) data.coCustomer = data.coCustomer.getSanitizedData();
	if (data.application?.getSanitizedData) data.application = data.application.getSanitizedData();

	return data;
}

export default {
	get: <T = any>(url: string, config?: any) => {
		return httpClient.get<T>(settings.apiUrl + url, config);
	},
	post: async <TRequest = any, TResponse = any>(url: string, data?: TRequest, config?: any) => {
		data = sanitizeData(data);
		return httpClient.post<TRequest, AxiosResponse<TResponse>>(settings.apiUrl + url, data, config);
	},
	put: async <TRequest = any, TResponse = any>(url: string, data: any, config?: any) => {
		data = sanitizeData(data);
		return httpClient.put<TRequest, AxiosResponse<TResponse>>(settings.apiUrl + url, data, config);
	},
	download: (url: string, fileName?: string) => {
		return httpClient
			.get(settings.apiUrl + url, {
				responseType: 'blob',
			})
			.then(response => {
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				link.href = url;

				const fileNameToUse = getFileNameFromHeaders(response.headers, fileName);

				link.setAttribute('download', fileNameToUse); //or any other extension
				document.body.appendChild(link);
				link.click();
			});
	},
	downloadPost: (url: string, data: any, fileName?: string) => {
		data = sanitizeData(data);
		return httpClient({
			url: settings.apiUrl + url,
			method: 'POST',
			data: data,
			responseType: 'blob',
		}).then(response => {
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;

			const fileNameToUse = getFileNameFromHeaders(response.headers, fileName);

			link.setAttribute('download', fileNameToUse);
			document.body.appendChild(link);
			link.click();
		});
	},
	getMsServer: () => {
		return settings.mediaServiceApiUrl;
	},
	getMs: (url: string, config?: any) => {
		if (config) return httpClient.get(settings.mediaServiceApiUrl + url, config);
		else return httpClient.get(settings.mediaServiceApiUrl + url);
	},
	postMs: (url: string, data: any) => {
		data = sanitizeData(data);
		return httpClient.post(settings.mediaServiceApiUrl + url, data);
	},
	getSB: (url: string, config?: any) => {
		if (config) return httpClient.get(settings.appDomain + '/api' + url, config);
		else return httpClient.get(settings.appDomain + '/api' + url);
	},
	getCancellationToken(tokenId: string) {
		if (!tokenId) {
			return null;
		}

		const CancelToken = axios.CancelToken;
		const source = CancelToken.source();
		cancelTokens[tokenId] = source;

		return source.token;
	},
	cancel: (tokenId: string) => {
		if (!cancelTokens[tokenId]) {
			return false;
		}

		cancelTokens[tokenId].cancel('Operation canceled by the user.');
		cancelTokens[tokenId] = null;

		return true;
	},
	getSpecial: <T = any>(url: string, config?: AxiosRequestConfig) => {
		return httpClient.get<T>(settings.saturnSpecialApiUrl + url, config);
	},
	postSpecial: async <TRequest = any, TResponse = any>(url: string, data?: TRequest, config?: AxiosRequestConfig) => {
		data = sanitizeData(data);
		return httpClient.post<TRequest, AxiosResponse<TResponse>>(settings.saturnSpecialApiUrl + url, data, config);
	},
};

function getFileNameFromHeaders(headers: any, fileName?: string) {
	let fileNameToUse = 'downloaded-file';

	if (!fileName) {
		const disposition = headers['content-disposition'];

		if (disposition) {
			const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
			if (match && match[1]) {
				fileNameToUse = match[1].replace(/['"]/g, '');
			}
		}
	} else {
		fileNameToUse = fileName;
	}

	return fileNameToUse;
}
